'use client'

import { motion } from '@/lib/framer-motion-shim'
import Image from 'next/image'
import { useI18n } from '@/lib/i18n/context'

interface TestimonialCardProps {
  quote: string
  author: string
  role?: string
  company?: string
  avatar?: { url: string; alt?: string }
  rating?: number
  index?: number
}

export default function TestimonialCard({
  quote,
  author,
  role,
  company,
  avatar,
  rating,
  index = 0,
}: TestimonialCardProps) {
  const { locale } = useI18n()
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.15 }}
      className="card-base p-6 md:p-8 flex flex-col group"
      aria-label={`${locale === 'en' ? 'Testimonial by' : 'Témoignage de'} ${author}${role ? ` — ${role}` : ''}`}
      role="article"
    >
      {/* Stars */}
      {typeof rating === 'number' && (
        <div className="flex gap-1 mb-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <svg
              key={i}
              aria-hidden="true"
              focusable="false"
              className={`w-4 h-4 ${i < rating ? 'text-amber' : 'text-gray-200'}`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          ))}
        </div>
      )}

      {/* Citation d'un tiers : guillemets ouvrants ET fermants dans le texte
          affiché (pas seulement décoratifs) + attribution nommée juste en
          dessous — ce qui permet à une citation de garder les mots du client
          (« neutralité », par ex.) sans que ce soit lu comme une allégation
          de Stock CO2. Si l'un des deux manque, ce n'est plus une citation
          reconnaissable : voir src/lib/projets/regles-contenu.ts. */}
      <blockquote className="text-gray-600 text-sm md:text-base leading-relaxed flex-1 relative">
        <span className="absolute -top-2 -left-1 text-4xl text-forest/10 font-serif" aria-hidden="true">&ldquo;</span>
        <p className="relative z-10 pl-4">« {quote} »</p>
      </blockquote>

      {/* Author */}
      <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-50">
        {avatar?.url ? (
          <Image
            src={avatar.url}
            alt={avatar.alt || author}
            width={40}
            height={40}
            className="rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-sage flex items-center justify-center font-heading font-bold text-forest text-sm transition-transform duration-300 group-hover:scale-110">
            {author.charAt(0)}
          </div>
        )}
        <footer>
          <cite className="font-heading font-semibold text-sm text-gray-900 not-italic block">{author}</cite>
          {(role || company) && (
            <p className="text-xs text-gray-500">
              {role}{role && company ? ' — ' : ''}{company}
            </p>
          )}
        </footer>
      </div>
    </motion.div>
  )
}
